import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, FileText, ChevronRight, Users } from 'lucide-react';
import { supabase } from '../../lib/supabase';

type ClassRow = {
  id: string;
  name: string;
  class_number: number;
  period: string;
  student_count: number;
};

const AdminLivretsIndex = () => {
  const [classes, setClasses] = useState<ClassRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: classesData } = await supabase
          .from('classes')
          .select('id, name, class_number, period')
          .eq('archived', false)
          .order('name');
        const { data: studentsData } = await supabase.from('students').select('class_id');
        const counts = new Map<string, number>();
        (studentsData ?? []).forEach((s: any) => {
          if (s.class_id) counts.set(s.class_id, (counts.get(s.class_id) ?? 0) + 1);
        });
        setClasses(
          (classesData ?? []).map((c: any) => ({
            id: c.id,
            name: c.name,
            class_number: c.class_number,
            period: c.period,
            student_count: counts.get(c.id) ?? 0,
          }))
        );
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };
    void load();
  }, []);

  return (
    <div className="animate-fade-in">
      <header className="mb-8">
        <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-2">
          <ChevronLeft className="h-4 w-4 mr-1" />
          Retour au tableau de bord
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <FileText className="h-7 w-7 text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-900">Livrets scolaires</h1>
        </div>
        <p className="text-gray-600">
          Choisissez une classe pour générer en lot les livrets scolaires (ZIP) de tous les candidats.
        </p>
      </header>

      {isLoading ? (
        <div className="p-8 text-center text-gray-500">Chargement...</div>
      ) : classes.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          Aucune classe active.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {classes.map((c) => (
            <Link
              key={c.id}
              to={`/admin/livrets/${c.id}`}
              className="bg-white rounded-lg shadow p-5 hover:shadow-md transition group"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-semibold text-gray-900 group-hover:text-primary-600 transition">
                    {c.name}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {c.name} {c.class_number} ({c.period})
                  </p>
                  <p className="text-sm text-gray-600 mt-3 inline-flex items-center">
                    <Users className="h-4 w-4 mr-1" />
                    {c.student_count} étudiant{c.student_count > 1 ? 's' : ''}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-primary-600 transition" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminLivretsIndex;
